import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertCircle, Star, CreditCard, Globe, Bitcoin } from 'lucide-react';
import { botServices } from './ServicesSection';
import { useTelegram } from '@/hooks/useTelegram';
import { trpc } from '@/providers/trpc';
import type { PaymentMethodOrPending } from '../../contracts/enums';

interface OrderModalProps {
  isOpen: boolean;
  serviceId: string | null;
  price: number;
  onClose: () => void;
}

const paymentMethods: {
  id: PaymentMethodOrPending;
  icon: React.ReactNode;
  color: string;
}[] = [
  { id: 'pending', icon: <CheckCircle size={18} />, color: '#8A93A7' },
  { id: 'stars', icon: <Star size={18} />, color: '#FFB020' },
  { id: 'ru_card', icon: <CreditCard size={18} />, color: '#00E5A0' },
  { id: 'intl_card', icon: <Globe size={18} />, color: '#00D4FF' },
  { id: 'crypto', icon: <Bitcoin size={18} />, color: '#8B5CF6' },
];

const paymentLabelKey: Record<PaymentMethodOrPending, string> = {
  pending: 'order.paymentNote',
  stars: 'order.paymentStars',
  ru_card: 'order.paymentRuCard',
  intl_card: 'order.paymentIntlCard',
  crypto: 'order.paymentCrypto',
};

type Step = 'form' | 'success' | 'error';

export default function OrderModal({
  isOpen,
  serviceId,
  price,
  onClose,
}: OrderModalProps) {
  const { t } = useTranslation();
  const { user, isInTelegram, hapticNotify, sendData, showBackButton, hideBackButton } = useTelegram();
  const [step, setStep] = useState<Step>('form');
  const [description, setDescription] = useState('');
  const [budgetIndex, setBudgetIndex] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOrPending>('pending');

  const createOrder = trpc.order.create.useMutation();
  const service = botServices.find(s => s.id === serviceId);
  const budgets = t('order.budgets', { returnObjects: true }) as string[];

  // Reset state every time modal opens fresh.
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setDescription('');
      setBudgetIndex(null);
      setPaymentMethod('pending');
      createOrder.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Telegram BackButton when modal is open
  useEffect(() => {
    if (!isOpen) return;
    showBackButton(onClose);
    return () => hideBackButton();
  }, [isOpen, onClose, showBackButton, hideBackButton]);

  if (!isOpen) return null;

  const serviceName = service ? t(`services.bots.${service.id}.name`) : 'Custom Bot';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isInTelegram) return;
    try {
      const { orderId } = await createOrder.mutateAsync({
        serviceId: service?.id ?? 'custom',
        serviceName,
        description: description.trim() || undefined,
        price,
        budgetIndex: budgetIndex ?? undefined,
        paymentMethod,
      });
      hapticNotify('success');
      // Notify the bot so it can confirm in chat and notify admins
      sendData({ type: 'order_confirmed', orderId });
      setStep('success');
    } catch {
      hapticNotify('error');
      setStep('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div
        className="relative w-full max-w-[480px] max-h-[92vh] overflow-y-auto glass-modal rounded-t-3xl sm:rounded-2xl p-6 sm:p-7 z-10 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 glass rounded-full flex items-center justify-center text-[#8A93A7] hover:text-[#F0F2F5] transition-colors z-10"
        >
          <X size={16} />
        </button>

        {step === 'success' ? (
          <SuccessState onClose={onClose} t={t} />
        ) : step === 'error' ? (
          <ErrorState
            onRetry={() => setStep('form')}
            onClose={onClose}
            t={t}
            message={t('order.error')}
          />
        ) : (
          <>
            <h3 className="text-[#F0F2F5] text-lg font-bold mb-1">
              {t('order.title')}
            </h3>
            <p className="text-[#8A93A7] text-xs mb-5">
              {t('order.serviceLabel')}:{' '}
              <span className="text-[#F0F2F5]">{serviceName}</span> ·{' '}
              <span className="text-[#00D4FF] font-bold">
                ${price.toLocaleString()}
              </span>
            </p>

            {!isInTelegram && (
              <div className="mb-4 p-3 rounded-xl glass-card flex items-start gap-2 text-[12px] text-[#FFB020]">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{t('order.notInTelegram')}</span>
              </div>
            )}

            {user && (
              <div className="mb-4 p-3 rounded-xl glass-card text-[11px] text-[#B5BBC9] leading-relaxed">
                <p>
                  Telegram:{' '}
                  <span className="text-[#F0F2F5] font-medium">
                    {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                  </span>
                  {user.username && (
                    <span className="text-[#8A93A7]"> · @{user.username}</span>
                  )}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[#8A93A7] text-[11px] font-medium uppercase tracking-wider mb-1.5 block">
                  {t('order.description')}
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={t('order.descPlaceholder')}
                  rows={4}
                  maxLength={2000}
                  required
                  className="w-full glass-input rounded-xl px-4 py-3 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50 resize-none"
                />
              </div>

              <div>
                <label className="text-[#8A93A7] text-[11px] font-medium uppercase tracking-wider mb-2 block">
                  {t('order.budget')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {budgets.map((option, i) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setBudgetIndex(i)}
                      className={`h-10 rounded-lg text-[11px] font-semibold transition-all px-2
                        ${budgetIndex === i ? 'bg-[#00D4FF] text-[#0B0E14] shadow-lg shadow-[rgba(0,212,255,0.25)]' : 'glass-input text-[#B5BBC9] hover:text-[#F0F2F5]'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[#8A93A7] text-[11px] font-medium uppercase tracking-wider mb-2 block">
                  {t('order.paymentTitle')}
                </label>
                <p className="text-[#8A93A7] text-[11px] mb-2.5">
                  {t('order.paymentMethodNote')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setPaymentMethod(method.id)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left text-[12px] transition-all
                        ${paymentMethod === method.id ? 'border-[rgba(0,212,255,0.4)] bg-[rgba(0,212,255,0.06)]' : 'border-[rgba(255,255,255,0.08)] glass-card'}`}
                    >
                      <span style={{ color: method.color }}>{method.icon}</span>
                      <span className="text-[#F0F2F5] text-[12px]">
                        {method.id === 'pending'
                          ? t('order.paymentNote').split('.')[0]
                          : t(paymentLabelKey[method.id])}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={createOrder.isPending || !isInTelegram}
                className="w-full h-12 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createOrder.isPending ? t('order.creating') : `${t('order.submit')} →`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function SuccessState({
  onClose,
  t,
}: {
  onClose: () => void;
  t: (k: string) => string;
}) {
  return (
    <div className="text-center py-10">
      <div
        className="w-20 h-20 rounded-full bg-[rgba(0,229,160,0.1)] flex items-center justify-center mx-auto mb-5 animate-scale-in"
        style={{ boxShadow: '0 0 40px rgba(0,229,160,0.2)' }}
      >
        <CheckCircle size={36} className="text-[#00E5A0]" />
      </div>
      <h3 className="text-[#00E5A0] text-xl font-bold mb-2">{t('order.success')}</h3>
      <p className="text-[#B5BBC9] text-sm leading-relaxed max-w-[320px] mx-auto mb-6">
        {t('order.successMsg')}
      </p>
      <button
        onClick={onClose}
        className="h-11 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl px-8"
      >
        {t('order.successCta')}
      </button>
    </div>
  );
}

function ErrorState({
  onRetry,
  onClose,
  t,
  message,
}: {
  onRetry: () => void;
  onClose: () => void;
  t: (k: string) => string;
  message: string;
}) {
  return (
    <div className="text-center py-10">
      <div className="w-20 h-20 rounded-full bg-[rgba(255,71,87,0.1)] flex items-center justify-center mx-auto mb-5">
        <AlertCircle size={36} className="text-[#FF4757]" />
      </div>
      <p className="text-[#F0F2F5] text-sm mb-6">{message}</p>
      <div className="flex gap-2 justify-center">
        <button
          onClick={onClose}
          className="h-10 px-5 rounded-lg text-[#B5BBC9] text-sm glass-btn-secondary"
        >
          {t('order.back')}
        </button>
        <button
          onClick={onRetry}
          className="h-10 px-5 glass-btn text-[#0B0E14] font-bold text-sm rounded-lg"
        >
          {t('order.submit')}
        </button>
      </div>
    </div>
  );
}
